/* eslint-disable react-compiler/react-compiler -- useEffect inside Formik render prop is necessary for form validation */
import Box from "@mui/material/Box";
import { Formik, Form, FormikProps } from "formik";
import { RefObject, useEffect } from "react";
import * as yup from "yup";

import addReserve from "@/features/reserves/api/addReserve";
import { ReserveBaseModel } from "@/features/reserves/types/reserveModels";
import { RNumberFormInput, RTextFormInput } from "@/shared/components/forms";
import {
  FIELD_LENGTH,
  FIELD_REQUIRED,
  LONG_NAMES,
  LONG_DESCRIPTION,
  MID_AMOUNT_RANGE,
  DURATION_RANGE,
  EMISSION_RANGE,
} from "@/shared/constants";
import useProjectWBSContext from "@/shared/context/projectWBS/useProjectWBSContext";
import { validateFormFields } from "@/shared/utils/formValidation";

const validationSchema = yup.object({
  name: yup.string().required(FIELD_REQUIRED).max(LONG_NAMES, FIELD_LENGTH),
  description: yup.string().max(LONG_DESCRIPTION, FIELD_LENGTH),
});

export type CreateReserveFormProps = {
  innerRef: RefObject<FormikProps<ReserveBaseModel>>;
  setIsFormValid?: (isValid: boolean) => void;
};

export default function CreateReserveForm({
  innerRef,
  setIsFormValid,
}: CreateReserveFormProps) {
  const { getWBSEntryById } = useProjectWBSContext();
  const WBSEntry = getWBSEntryById();

  const initialValues: ReserveBaseModel = {
    id: "",
    projectId: WBSEntry?.projectId ?? "",
    name: "",
    description: "",
    cost: 0,
    duration: 0,
    emissions: 0,
  };

  return (
    <Formik
      initialValues={initialValues}
      innerRef={innerRef}
      validationSchema={validationSchema}
      onSubmit={async (values, { setStatus }) => {
        try {
          await addReserve(values);
        } catch (err) {
          if (err instanceof Error) {
            setStatus({ errorMessage: err.message });
            setTimeout(() => setStatus(null), 5000);
          }
        }
      }}>
      {({
        setFieldTouched,
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        validateField,
        setFieldValue,
        isValid,
      }) => {
        // Move validateFormFields to useEffect to avoid calling setState during render
        // eslint-disable-next-line react-hooks/rules-of-hooks -- Formik render prop pattern requires hooks inside
        useEffect(() => {
          validateFormFields<ReserveBaseModel>(
            isValid,
            touched,
            ["name"],
            setIsFormValid
          );
        }, [isValid, touched]);

        return (
          <Box sx={{ "& .MuiTextField-root": { mb: 2 }, paddingTop: "1rem" }}>
            <Form>
              <RTextFormInput
                required
                name="name"
                label="Reserve Name"
                value={values.name}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldTouched={setFieldTouched}
                validateField={validateField}
                maxLength={LONG_NAMES}
              />
              <RTextFormInput
                name="description"
                label="Description"
                value={values.description}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldTouched={setFieldTouched}
                validateField={validateField}
                maxLength={LONG_DESCRIPTION}
              />
              <RNumberFormInput
                name="cost"
                label="Cost"
                value={values.cost}
                allowNegative
                category="currency"
                range={MID_AMOUNT_RANGE}
                decimalScale={6}
                onInputChange={(newValue) => {
                  void setFieldValue("cost", newValue);
                }}
              />
              <RNumberFormInput
                name="duration"
                label="Duration"
                value={values.duration}
                allowNegative
                range={DURATION_RANGE}
                decimalScale={6}
                onInputChange={(newValue) => {
                  void setFieldValue("duration", newValue);
                }}
                category="duration"
              />
              <RNumberFormInput
                name="emissions"
                label="Emissions"
                value={values.emissions}
                allowNegative
                range={EMISSION_RANGE}
                decimalScale={6}
                onInputChange={(newValue) => {
                  void setFieldValue("emissions", newValue);
                }}
                category="emissions"
              />
            </Form>
          </Box>
        );
      }}
    </Formik>
  );
}
